import React from 'react';
import { clsx } from 'clsx';
import { signedToneClass, moneyAbbrev, SPARK_NEUTRAL } from './history-utils';
import { MiniSparkline } from './MiniSparkline';

// LR-498 (+ v2 T1-2): the "By counterparty" master list. Each counterparty appears ONCE as a
// non-clickable group header; its currency "books" are indented, selectable sub-rows beneath it
// (a prime-broker mental model: one counterparty/agreement → several currency books). MTM books
// show the latest-run net; Cash Pool books show the running balance + a trend sparkline. Selecting a
// book drives the right pane to that CP × currency's dated ledger. The host groups the rows by name.

export interface CpListRow {
  /** Selection key — "<counterparty_name>|<currency>" (MTM), the counterparty name (Cash Pool). */
  key: string;
  cp: string;
  /** Currency code of this book — rendered as a fixed-width tag, no longer baked into `meta` (T1-1). */
  currency: string;
  /** Sub-line, e.g. "5 runs · last 24 Jun" or "last call today · 16:30" (currency removed). */
  meta: string;
  /** Headline figure — latest-run net (MTM) or running balance (Cash Pool). */
  value: number;
  /** Caption under the figure — "latest run" (MTM) or the currency code (Cash Pool). */
  valueCaption: string;
  /** True when |value| is below the CP's MTA → muted with a "below MTA" caption (T1-3, MTM only). */
  belowMta?: boolean;
  /**
   * LR-542 (feedback #63, variant A): 'neutral' renders the FIGURE without green/red sign
   * colours — used by Cash Pool rows, whose billing-signed running balance is a ledger figure,
   * not an in-our-favour reading. Default 'signed' (MTM latest-run net). The sparkline is not
   * governed by this: its stroke is unconditionally SPARK_NEUTRAL (see the comment above it).
   */
  valueTone?: 'signed' | 'neutral';
  /** Recent values oldest→newest for the trend sparkline (Cash Pool only). */
  spark?: number[];
}

/** A counterparty and its currency books, grouped + order-preserved by the host. */
export interface CpGroup {
  name: string;
  rows: CpListRow[];
}

// LR-542 review kvi #2/#3: the sparkline's only producer is the Cash Pool rail (HistoryShell),
// whose rows are variant-A neutral — so the stroke is unconditionally the CONTRAST-SAFE neutral
// (SIGN_COLOR.neutral fails WCAG 1.4.11 under the 0.85 stroke opacity; see SPARK_NEUTRAL). The
// former signed branches were unreachable. If MTM rows ever grow sparks, reintroduce a per-row
// tone here rather than reaching for SIGN_COLOR.neutral.

const HEADER_TAG_TONE = {
  accent: 'bg-lr-accent-weak text-lr-accent',
  warn: 'bg-lr-warn-weak text-lr-warn',
} as const;

export interface CounterpartyListProps {
  groups: CpGroup[];
  selectedKey?: string;
  onSelect: (key: string) => void;
  /** Tab-level bucket tag on each group header (MTM "Stock L/B" / Cash Pool "Repo cash pool"). */
  headerTag?: { label: string; tone: keyof typeof HEADER_TAG_TONE };
  emptyLabel?: string;
  /** "N currencies" on the header. Off where a counterparty has one row whatever its currencies. */
  showBookCount?: boolean;
}

export const CounterpartyList: React.FC<CounterpartyListProps> = ({
  groups,
  selectedKey,
  onSelect,
  headerTag,
  emptyLabel = 'No results.',
  showBookCount = true,
}) => {
  if (groups.length === 0) {
    return <p className="px-[18px] py-3 text-sm text-lr-faint">{emptyLabel}</p>;
  }
  return (
    <div>
      {groups.map((group) => (
        <section key={group.name}>
          {/* Non-clickable counterparty header — the name appears once, books are listed below. */}
          <div className="flex items-center gap-2 border-b border-lr-border/60 px-[15px] pt-3 pb-1.5">
            <span className="min-w-0 flex-1 truncate text-[12.5px] font-bold text-lr-fg">
              {group.name}
            </span>
            {headerTag && (
              <span
                className={clsx(
                  'shrink-0 rounded px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-[.04em]',
                  HEADER_TAG_TONE[headerTag.tone]
                )}
              >
                {headerTag.label}
              </span>
            )}
            {showBookCount && (
              <span className="shrink-0 text-[10.5px] text-lr-faint">
                {group.rows.length} {group.rows.length === 1 ? 'currency' : 'currencies'}
              </span>
            )}
          </div>
          {group.rows.map((row) => {
            const selected = row.key === selectedKey;
            return (
              <button
                key={row.key}
                type="button"
                onClick={() => onSelect(row.key)}
                aria-current={selected}
                className={clsx(
                  'flex w-full items-center gap-3 border-b border-lr-border/60 border-l-[3px] py-[11px] pl-[15px] pr-[18px] text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-lr-accent',
                  selected
                    ? 'border-l-lr-accent bg-lr-row-active'
                    : 'border-l-transparent hover:bg-lr-surface-2'
                )}
              >
                <span className="w-[46px] shrink-0 rounded bg-[#f1f3f5] px-1.5 py-0.5 text-center font-mono text-[10.5px] text-lr-fg-2">
                  {row.currency}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[11.5px] text-lr-faint">{row.meta}</div>
                </div>
                {row.spark && row.spark.length > 0 && (
                  <MiniSparkline values={row.spark} color={SPARK_NEUTRAL} className="shrink-0" />
                )}
                <div className="shrink-0 text-right">
                  <div
                    className={clsx(
                      'font-mono text-[13.5px] font-semibold',
                      row.belowMta
                        ? 'text-lr-faint'
                        : row.valueTone === 'neutral'
                          ? 'text-lr-fg'
                          : signedToneClass(row.value)
                    )}
                  >
                    {moneyAbbrev(row.value)}
                  </div>
                  <div className="mt-0.5 text-[10px] uppercase tracking-[.03em] text-lr-faint">
                    {row.belowMta ? 'below MTA' : row.valueCaption}
                  </div>
                </div>
              </button>
            );
          })}
        </section>
      ))}
    </div>
  );
};
